import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiKeyService } from '../../services/apiKeyService';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const CreateApiKey = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const response = await apiKeyService.createApiKey(name);
      setCreatedKey(response.api_key);
    } catch (err) {
      setError('Erro ao criar API Key. Tente novamente.');
      console.error('Error creating API key:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate('/api-keys');
  };

  const handleCopyKey = async () => {
    if (createdKey) {
      try {
        await navigator.clipboard.writeText(createdKey);
        setShowCopySuccess(true);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
        Criar Nova API Key
      </Typography>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Nome da API Key"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={loading}
            sx={{ mb: 3 }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !name}
            >
              {loading ? <CircularProgress size={24} /> : 'Criar'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/api-keys')}
              disabled={loading}
            >
              Cancelar
            </Button>
          </Box>
        </form>
      </Paper>

      <Dialog 
        open={!!createdKey} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>API Key Criada com Sucesso</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Atenção: Copie esta API Key agora. Você não poderá visualizá-la novamente depois.
          </Alert>
          <Box sx={{ 
            mt: 2, 
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Chip
              label={createdKey}
              sx={{ 
                flex: 1,
                fontFamily: 'monospace',
                fontSize: '1.1rem',
                height: 'auto',
                '& .MuiChip-label': {
                  whiteSpace: 'break-spaces',
                  padding: '8px',
                }
              }}
            />
            <Tooltip title="Copiar API Key">
              <IconButton
                onClick={handleCopyKey}
                size="small"
                sx={{ ml: 1 }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showCopySuccess}
        autoHideDuration={3000}
        onClose={() => setShowCopySuccess(false)}
        message="API Key copiada com sucesso!"
      />
    </Box>
  );
};

export default CreateApiKey; 